/**
 * Web page block in Loro (spec §7.4.12) and the frame protocol (§8.16). Pure: no React, no network.
 *
 * Body: `url` (committed URL, LWW, watched), `caption` (LoroText), `frozen` (plain SnapshotRef | null,
 * LWW, watched), `snapshots` (LoroMap<id, plain SnapshotRef>, every freeze), `height` (px).
 *
 * `[M21]` `height` here is legacy (§7.4.12): the frame size of pages authored before the generic
 * block height of §7.3 existed. It is still read, so those pages look as they did, and never
 * written — the grip of §8.6 item 8 writes the node-level key instead.
 */
import { LoroMap, LoroText, type LoroDoc } from "loro-crdt";
import type { SnapshotRef } from "@kss/schema";
import { v7 as uuidv7 } from "uuid";

export const DEFAULT_HEIGHT = 600;
export const MIN_HEIGHT = 200;
export const MAX_HEIGHT = 2400;

export interface WebPageState {
  url: string;
  frozen: SnapshotRef | null;
  /** oldest first (Q28) */
  snapshots: Array<{ id: string; ref: SnapshotRef }>;
  height: number;
}

export function initWebPageBody(body: LoroMap, init?: unknown): void {
  const url = typeof (init as { url?: unknown } | undefined)?.url === "string" ? normalizeUrl((init as { url: string }).url) : null;
  body.set("url", url ?? "");
  body.setContainer("caption", new LoroText());
  body.set("frozen", null);
  body.setContainer("snapshots", new LoroMap());
  body.set("height", DEFAULT_HEIGHT);
}

function isSnapshotRef(v: unknown): v is SnapshotRef {
  const r = v as SnapshotRef | null;
  return !!r && typeof r === "object" && typeof r.asset?.hash === "string" && /^[0-9a-f]{64}$/.test(r.asset.hash) && typeof r.capturedAt === "number";
}

export function readWebPage(body: LoroMap): WebPageState {
  const snapsMap = body.get("snapshots");
  const snapshots: Array<{ id: string; ref: SnapshotRef }> = [];
  if (snapsMap instanceof LoroMap) {
    for (const [id, ref] of Object.entries(snapsMap.toJSON() as Record<string, unknown>)) if (isSnapshotRef(ref)) snapshots.push({ id, ref });
  }
  snapshots.sort((a, b) => a.ref.capturedAt - b.ref.capturedAt || a.id.localeCompare(b.id));
  const frozen = body.get("frozen");
  const height = body.get("height");
  return {
    url: typeof body.get("url") === "string" ? (body.get("url") as string) : "",
    frozen: isSnapshotRef(frozen) ? frozen : null,
    snapshots,
    height: typeof height === "number" && height > 0 ? Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, height)) : DEFAULT_HEIGHT,
  };
}

/** A typed address as an http(s) URL, or null. A bare host gets `https://`; other schemes are refused. */
export function normalizeUrl(input: string): string | null {
  const raw = input.trim();
  if (!raw) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  try {
    const u = new URL(withScheme);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    if (!u.hostname || /\s/.test(raw)) return null;
    return u.href;
  } catch {
    return null;
  }
}

/** Address bar or **Use this page**: the URL every reader opens the block at (Q26). */
export function commitUrl(doc: LoroDoc, body: LoroMap, url: string): boolean {
  const normalized = normalizeUrl(url);
  if (!normalized || body.get("url") === normalized) return false;
  body.set("url", normalized);
  doc.commit();
  return true;
}

/** Freeze: the new snapshot goes into history and becomes `frozen`, in one commit (§8.16). */
export function addSnapshot(doc: LoroDoc, body: LoroMap, ref: SnapshotRef): string {
  const snaps = body.get("snapshots") instanceof LoroMap ? (body.get("snapshots") as LoroMap) : body.setContainer("snapshots", new LoroMap());
  const id = uuidv7();
  snaps.set(id, ref);
  body.set("frozen", ref);
  doc.commit();
  return id;
}

/** **Show this snapshot** re-freezes an earlier one; `null` unfreezes (history is kept). */
export function setFrozen(doc: LoroDoc, body: LoroMap, ref: SnapshotRef | null): void {
  body.set("frozen", ref);
  doc.commit();
}

export const sameSnapshot = (a: SnapshotRef | null, b: SnapshotRef | null) => !!a && !!b && a.asset.hash === b.asset.hash && a.capturedAt === b.capturedAt;

/** A WARC timestamp (`YYYYMMDDhhmmss`, UTC) for the replay app's `ts`. */
export function waybackTs(ms: number): string {
  return new Date(ms).toISOString().replace(/[-:T]/g, "").slice(0, 14);
}

// --- frame protocol --------------------------------------------------------------------------------

export type LocationMessage = { type: "kss:location"; url: string; title: string };
export type SnapshotResultMessage = { type: "kss:snapshot-result"; id: number; url: string; title: string; dom: string };

/** A message from the live frame, accepted only from the web origin and that frame's window. */
export function liveMessage(event: MessageEvent, webOrigin: string, frame: Window | null | undefined): LocationMessage | SnapshotResultMessage | null {
  if (event.origin !== webOrigin || !frame || event.source !== frame) return null;
  const d = event.data as Record<string, unknown> | null;
  if (!d || typeof d !== "object") return null;
  if (d.type === "kss:location" && typeof d.url === "string" && /^https?:\/\//.test(d.url)) return { type: "kss:location", url: d.url, title: typeof d.title === "string" ? d.title : "" };
  if (d.type === "kss:snapshot-result" && typeof d.id === "number" && typeof d.url === "string" && typeof d.dom === "string")
    return { type: "kss:snapshot-result", id: d.id, url: d.url, title: typeof d.title === "string" ? d.title : "", dom: d.dom };
  return null;
}
